import type { Stage } from '@botguide/shared';
import { ACTIVE_STAGES, STAGE_LABELS } from '@botguide/shared';

import { cn } from '@/lib/cn';

import { Badge } from './Badge';

/** Same contract as lanes: one token per enum value, `--stage-<value>`. */
export function stageColour(stage: Stage): string {
  return `var(--stage-${stage})`;
}

export function stageColours(stage: Stage): { fill: string; ink: string; edge: string } {
  return {
    fill: `var(--stage-${stage}-soft)`,
    // The soft tints are light in light mode, so the solid hue is the ink.
    ink: `color-mix(in srgb, var(--stage-${stage}) 82%, var(--ink))`,
    edge: `color-mix(in srgb, var(--stage-${stage}) 30%, transparent)`,
  };
}

export function StageBadge({
  stage,
  size = 'md',
  className,
}: {
  stage: Stage;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <Badge tone="custom" colours={stageColours(stage)} size={size} className={className}>
      {STAGE_LABELS[stage]}
    </Badge>
  );
}

/**
 * The seven SDLC stages as a track, with the current one marked.
 *
 * Position carries the meaning here, not just colour: the filled portion is
 * how far along the work is, and the current stage is also named in text
 * beneath. Only meaningful while a task is `in_progress`, which is the only
 * lane that carries an active stage.
 */
export function StageTrack({
  stage,
  className,
}: {
  stage: Stage | null;
  className?: string;
}) {
  const index = stage ? ACTIVE_STAGES.indexOf(stage as (typeof ACTIVE_STAGES)[number]) : -1;

  return (
    <div className={cn('min-w-0', className)}>
      <ol
        className="flex items-center gap-1"
        aria-label={stage ? `Stage ${STAGE_LABELS[stage]}` : 'No stage'}
      >
        {ACTIVE_STAGES.map((candidate, i) => {
          const done = index >= 0 && i < index;
          const current = index === i;
          return (
            <li
              key={candidate}
              title={STAGE_LABELS[candidate]}
              className={cn('h-1 flex-1 rounded-full', !done && !current && 'bg-line')}
              style={
                done || current
                  ? { backgroundColor: stageColour(candidate), opacity: current ? 1 : 0.45 }
                  : undefined
              }
            />
          );
        })}
      </ol>
      <p className="mt-1.5 text-nano font-medium uppercase tracking-[0.1em] text-subtle">
        {stage ? STAGE_LABELS[stage] : 'Not started'}
      </p>
    </div>
  );
}
